import { useEffect, useMemo, useState } from "react";
import { getStorageArea } from "./storageArea";

export function useStorageBackedState<T>(
  key: string,
  defaultValue: T,
  storage: "session" | "local" | "memory" = "local"
): [T, (newValue: T) => void] {
  const storageArea = useMemo(() => getStorageArea(storage), [storage]);

  const [value, _setValue] = useState<T>(() => {
    const storedValue = storageArea.getItem(key);
    if (storedValue === null) {
      return defaultValue;
    }
    return JSON.parse(storedValue);
  });

  const setValue = (newValue: T) => {
    _setValue(newValue);
    storageArea.setItem(key, JSON.stringify(newValue));

    // update others with the same key and storage
    sync[`${key}-${storage}`]?.forEach((setter) => setter(newValue));
  };

  const syncKey = useMemo(() => `${key}-${storage}`, [key, storage]);

  // sync the state with other components with the same key and storage
  useEffect(() => {
    if (!sync[syncKey]) sync[syncKey] = [];
    sync[syncKey].push(_setValue);
    return () => {
      sync[syncKey] = sync[syncKey].filter((setter) => setter !== _setValue);
    };
  }, [syncKey]);

  return [value, setValue];
}

const sync: Record<string, ((value: any) => void)[]> = {};
